import React from 'react';
import { GenericModal } from 'src/components/Modals/GenericModal';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { CloseIcon } from 'src/components/SvgIcons/SvgIcons';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Divider, Typography, Box } from '@mui/material';
import { useProfileImportStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores';
import { useGetProfiles } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/hooks/useProfiles';
import { ExistingProfile } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/types/profileTypes';
import SearchSelector, {
  SearchOption,
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/components/SearchSelector';
import { useSearchSelectorStore } from './stores/searchSelectorStore';

// Can use the SearchOption type from SearchSelector directly
type ProfileOption = SearchOption;

const convertProfilesToOptions = (profiles: ExistingProfile[]): ProfileOption[] => {
  if (!profiles) return [];
  return profiles?.map((profile) => ({
    id: profile.profileId,
    label: profile.profileName,
    updatedAt: profile.updatedAt,
    updatedBy: profile.updatedBy,
    profileVersion: profile.version
  }));
};

const ModalContent = () => {
  const { setImportedProfile, closeSelectProfileModal } = useProfileImportStore();
  const { data: profiles, isLoadingProfiles } = useGetProfiles();
  const { selectedOption, setSelectedOption } = useSearchSelectorStore();
  
  const handleSave = () => {
    if (selectedOption) {
      const importedProfile = profiles?.data.find((profile) => profile.profileId === selectedOption.id);
      setImportedProfile(importedProfile);
      setSelectedOption(null);
      closeSelectProfileModal();
    }
  };

  const profileOptions = convertProfilesToOptions(profiles?.data || []);

  if (isLoadingProfiles) {
    return <p>Loading...</p>;
  }

  if (!profiles?.data || profiles?.data?.length === 0) {
    return <p>No profiles found</p>;
  }

  const handleProfileChange = (newValue: SearchOption | null) => {
    setSelectedOption(newValue);
  };

  return (
    <>
      <Flex alignItems="center" className="genericModal__header">
        <Text
          variant="h4"
          sx={{
            marginLeft: 'auto',
            marginRight: 'auto'
          }}
        >
          Select Content Profiles
        </Text>
        <CloseIcon
          style={{ cursor: 'pointer', width: '24px', height: '24px', marginBottom: 'auto' }}
          onClick={() => {
            closeSelectProfileModal();
            setSelectedOption(null);
          }}
        />
      </Flex>
      <Divider
        sx={{
          marginTop: '22px',
          position: 'relative',
          left: '-100px',
          width: '1000px'
        }}
      />
      <GenericModal.Body>
        <Flex flexDirection="column">
          <Typography
            variant="body1"
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              marginBottom: '8px'
            }}
          >
            Content Profiles
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              marginBottom: '16px'
            }}
          >
            Select a content profile to assign to the products for which you want to generate content.
          </Typography>
          <Box sx={{ marginLeft: '-12px', width: 'calc(100% + 16px)' }}>
            <SearchSelector
              options={profileOptions}
              value={selectedOption}
              onChange={handleProfileChange}
              placeholder="Search content profiles"
            />
          </Box>
        </Flex>
      </GenericModal.Body>
      <GenericModal.Footer>
        <Flex justifyContent="space-between">
          <SlButton onClick={closeSelectProfileModal}>Close</SlButton>
          <SlButton variant="contained" onClick={handleSave} disabled={!selectedOption}>
            Save
          </SlButton>
        </Flex>
      </GenericModal.Footer>
    </>
  );
};

export const SelectProfileModal = () => {
  const { isSelectProfileModalOpen, closeSelectProfileModal } = useProfileImportStore();

  return (
    <GenericModal
      open={isSelectProfileModalOpen}
      onClose={() => {
        closeSelectProfileModal();
      }}
      sx={{
        '& .MuiPaper-root': {
          width: '752px',
          height: '641px'
        },
        '& .genericModal__body': {
          gap: '16px',
          padding: '24px 28px'
        }
      }}
    >
      <ModalContent />
    </GenericModal>
  );
};
